import * as React from 'react';
import { useLocation } from 'react-router-dom';

import Tabs, { TabsProps } from 'antd/es/tabs';
import Alert from 'antd/es/alert';

import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import ExternalHtml from '@common/react/components/UI/ExternalHtml/ExternalHtml';

import { DoctorPage } from '@app/objects/Pages/DoctorPage';
import Reviews, { AllReviews } from '@app/components/Pages/Doctor/Reviews';
import ScheduleAppointment from '@app/components/UI/ScheduleAppointmentModal/ScheduleAppointment';
import { Doctor } from '@app/objects/Doctor';
import { User } from '@app/objects/User';
import Locations from '@app/components/Pages/Doctor/Locations';
import Insurance from '@app/components/Pages/Doctor/Insurance';
import { Location } from '@app/objects/Location';
import DoctorMenu from '@app/components/Pages/Doctor/DoctorMenu';
import InquiryForm from '@app/components/Forms/InquiryForm';
import LoginForm from '@app/components/UI/ScheduleAppointmentModal/LoginForm';
import Certificates from '@app/components/Pages/Doctor/Certificates';
import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';

import '@app/scss/pages/doctor.scss';

interface DoctorPortalProps {
	serverPage: DoctorPage;
}

const DoctorPortalComponent: React.FC<DoctorPortalProps> = (props) => {
	const { serverPage } = props;
	const content = serverPage?.item;
	const { getUser } = useApplicationContext();
	const user = getUser<User>();
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [tab, setTab] = React.useState(content.doctor.scheduleAppointment && content?.doctorProcedures?.length > 0 ? 'appointment' : 'inquiry');
	const location = useLocation();
	const [loginVisible, setLoginVisible] = React.useState(false);

	const toggle = () => setIsOpen(!isOpen);

	if (!content) {
		return <Loader />;
	}

	const locations = content.doctor.workingHoursLocations;
	const locationsForInquiry = content.doctor.locations;

	const {
		educationAndTraining,
		professionalMemberships,
		awardsAndPublications,
		languages,
		npi,
		certificates,
	} = content.doctor;

	const hasAdditionalInformation = !!educationAndTraining || !!professionalMemberships || !!awardsAndPublications
	|| (content && content.doctor?.globalPayers?.length > 0) || !!certificates?.length;

	const getLabel = (label: string, icon: string): React.ReactElement => <div>
		<i className={`fa ${icon}`} style={{ marginRight: '7px' }} />
		{label}
	</div>;

	const getItems: (showMobile: boolean) => TabsProps['items'] = (showMobile) => [
		{
			key: 'appointment',
			label: getLabel('Schedule Appointment', 'fa-calendar'),
			className: 'appointment-tab',
			children:
				content.doctor.scheduleAppointment ? <>
					<div className="contact-text">
						<Alert
							message={<>
								Don’t know procedure you need or convenient time for visit?
								<br />
								<a onClick={() => setTab('inquiry')}>Leave&nbsp;inquiry</a>
								{' '}
								and clinic front desk will contact you soon.
							</>}
							type="info"
						/>
					</div>
					<ScheduleAppointment
						showDoctor
						showMobile={showMobile}
						patient={user as unknown as User}
						doctorFixed
						doctors={[content.doctor as any]}
						selectedLocation={location?.state?.selectedLocation}
						scheduledTime={location?.state?.scheduledTime}
						doctor={content.doctor as unknown as Doctor}
						locationItems={locations as Array<Location>}
						asNearestDate={location?.state?.asNearestDate}
						doctorProcedures={content?.doctorProcedures}
						doctorBookedTime={content?.doctorBookedTime}
					/>
				</> : null,
			disabled: !content.doctor.scheduleAppointment,
		},
		{
			key: 'inquiry',
			label: getLabel('Send Inquiry', 'fa-envelope'),
			children: <div className="inquiry-wrapper">
				<div className="contact-text">
					<Alert
						message={<>
							{content.doctor.scheduleAppointment
								? <>
									Know time and procedure you need?
									<br />
									Just
									{' '}
									<a onClick={() => setTab('appointment')}>Schedule&nbsp;Appointment</a>
									, clinic front desk will contact you soon to confirm it.
								</>
								: <>
									Leave inquiry and clinic front desk will contact you soon for help.
								</>}
							{!user ? <>
								<br />
								Already have account? -
								{' '}
								<a onClick={() => setLoginVisible(true)}>Login</a>
								{' '}
								for fast fill form.
							</> : null}
						</>
						}
						type="info"
					/>
				</div>
				<InquiryForm
					doctor={content.doctor as any}
					doctorId={content.doctor.id}
					locations={locationsForInquiry}
					selectedLocationId={location?.state?.selectedLocationId}
				/>
			</div>,
		},
	];

	return <>
		<div className="site-headline doctor-portal">
			<LoginForm visible={loginVisible} handleClose={() => setLoginVisible(false)} afterLogin={() => setLoginVisible(false)} />
			<div className="doctor-portal-container">
				<div className="px-50 mb20 doctor-portal-content">
					<div className="max-content main-content">
						<div className="pull-right">
							<ShareLinks />
						</div>
						<BreadCrumbs
							links={[
								{
									title: 'Doctors',
									path: '/doctors',
								},
							]}
							title={content.doctor.nameEn}
						/>
						<DoctorMenu
							doctor={content.doctor}
							location={location}
						/>
						<div className="doctor-other-content">
							{content
								? (
									<Reviews
										reviews={content?.topReviews}
										appointment={content?.appointment}
										doctor={content.doctor}
									/>
								)
								: null}
							{content?.doctor?.portalContentEn && <div className="doctor-portal__about scroll-top" id="about">
								<h3>
									About
									{' '}
									{content.doctor.nameEn}
								</h3>

								{content.doctor.portalContentEn
									? <div className="inner-page__headline column-md-4 mb10">
										<div className="column-md-4">
											<ExternalHtml html={content.doctor.portalContentEn} />
										</div>
									</div> : <div className="mb10">-</div>
								}
							</div>}
							<div className="doctor-portal__additional">
								<div className="mb10">
									{hasAdditionalInformation && <>
										<h4>Additional Information</h4>
										{educationAndTraining
											? <>
												<h5><strong className="mr10">Education and Training</strong></h5>
												<p>{educationAndTraining}</p>
											</> : ''
										}
										{professionalMemberships
											? <>
												<h5><strong className="mr10">Professional Memberships </strong></h5>
												<p>{professionalMemberships}</p>
											</> : ''
										}
										{awardsAndPublications
											? <>
												<h5><strong className="mr10">Awards and Publications</strong></h5>
												<p>{awardsAndPublications}</p>
											</> : ''
										}
										{certificates?.length ? <>
											<h5><strong className="mr10">Certificates</strong></h5>
											<Certificates items={certificates} />
										</> : ''}
									</>}
									{languages?.length
										? <>
											<h5><strong className="mr10">Languages</strong></h5>
											<p>
												{languages.length > 0
													&& languages.map((item, i) => <span key={item.id}>
														<label>{item.language.name}</label>
														{i !== languages.length - 1 ? ', ' : ''}
													</span>)}
											</p>
										</> : ''
									}
									{npi
										? <>
											<h5><strong className="mr10">NPI</strong></h5>
											<p>{npi}</p>
										</> : ''
									}
									{content && content.doctor?.globalPayers?.length > 0
										? <Insurance list={content.doctor.globalPayers} /> : null}
								</div>
							</div>
							<div className="show-touch show-touch__block">
								<h3 id="bookOnline" className="scroll-top">Book Online</h3>
								<div className="schedule-appointment__form">
									<Tabs
										activeKey={tab}
										onChange={setTab}
										items={getItems(true)}
									/>
								</div>
							</div>
							{locationsForInquiry.length > 0 ? <h3 id="locations" className="scroll-top">Locations</h3> : null}
							<Locations locations={locationsForInquiry || []} />
							{content ? <AllReviews reviews={content?.reviews} doctor={content.doctor} /> : null}
						</div>
					</div>
					<div className="schedule-appointment__wrapper mobile-hide">
						<div className={`schedule-appointment__form ${
							isOpen ? 'schedule-appointment__open' : 'schedule-appointment__close'} ${
							tab === 'inquiry' ? 'schedule-appointment__inquiry-tab' : ''}`}
						>
							<button
								type="button"
								className="btn btn-sm btn-default schedule-appointment__button pull-right"
								onClick={toggle}
							>
								<span />
							</button>
							<Tabs
								activeKey={tab}
								onChange={setTab}
								items={getItems(false)}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</>;
};

const DoctorPortalComponentWithServer: React.FC = (props) => {
	return (<ServerPageProvider
		path="doctors"
		reloadByPathChange
		inner={(serverPage) =>
			<DoctorPortalComponent
				{...props as any}
				serverPage={serverPage}
			/>
		}
	/>);
};

export default DoctorPortalComponentWithServer;
